import type { FC, ReactNode } from 'react'
import type { GuideInfo } from '@/constants/guides'

import { ArrowRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'

interface GuidePageProps {
    guide: GuideInfo
}

const GuidePage: FC<GuidePageProps> = ({ guide }): ReactNode => {
    const primary = guide.links[0]

    return (
        <Layout>
            <Seo title={guide.title} description={guide.summary} />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <article className='mx-auto max-w-3xl px-6 pt-14 pb-20 md:pt-20'>
                    <nav aria-label='Breadcrumb' className='text-sm text-slate-500'>
                        <a href='/guides' className='hover:text-cyan-300'>Guides</a>
                        <span className='mx-2'>/</span>
                        <span className='text-slate-300'>Guide</span>
                    </nav>

                    <header id='hero' className='mt-6'>
                        <h1 className='text-3xl leading-tight font-extrabold tracking-tight text-white md:text-5xl'>{guide.title}</h1>
                        <p className='mt-5 text-lg leading-relaxed text-slate-400'>{guide.summary}</p>
                        <p className='mt-4 font-mono text-xs tracking-[0.15em] text-slate-500 uppercase'>Reviewed {guide.reviewed}</p>
                    </header>

                    <p className='mt-10 text-lg leading-relaxed text-slate-200'>{guide.intro}</p>

                    {primary && (
                        <a href={primary.href} className='mt-8 inline-flex items-center gap-2 rounded-xl bg-cyan-300 px-5 py-3 text-sm font-bold text-[#06121f] transition hover:bg-cyan-200'>
                            {primary.label} <ArrowRight className='size-4' />
                        </a>
                    )}

                    <div className='mt-12 space-y-10'>
                        {guide.sections.map((section) => (
                            <section key={section.heading}>
                                <h2 className='text-xl font-bold text-white md:text-2xl'>{section.heading}</h2>
                                {section.bullets && (
                                    <ul className='mt-4 list-disc space-y-2 pl-5 leading-relaxed text-slate-300 marker:text-cyan-300'>
                                        {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                                    </ul>
                                )}
                                {section.paragraphs?.map((paragraph) => (
                                    <p key={paragraph} className='mt-4 leading-relaxed text-slate-300'>{paragraph}</p>
                                ))}
                            </section>
                        ))}
                    </div>

                    {guide.closing && (
                        <p className='mt-12 border-l-2 border-amber-300 pl-5 text-lg leading-relaxed text-white'>{guide.closing}</p>
                    )}

                    <section id='try-it' className='mt-14 rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-8'>
                        <h2 className='text-lg font-bold text-white'>Try it</h2>
                        <ul className='mt-4 flex flex-wrap gap-3'>
                            {guide.links.map((link) => (
                                <li key={link.href}>
                                    <a href={link.href} className='inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-medium text-slate-200 transition hover:border-cyan-300/50'>
                                        {link.label} <ArrowRight className='size-4 text-cyan-300' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <p className='mt-10 text-sm text-slate-500'>
                        <a href='/guides' className='text-cyan-300 hover:underline'>All guides</a>
                        <span className='mx-2'>·</span>
                        <a href='/tools' className='text-cyan-300 hover:underline'>Free tools</a>
                        <span className='mx-2'>·</span>
                        <a href='/support' className='text-cyan-300 hover:underline'>Support</a>
                    </p>
                </article>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default GuidePage
